import { useMemo } from 'react';
import { Divider, Typography } from '@mui/material';

import { LinkChip, InfoField } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as loyaltyEventRouteCreator } from '@occc-app/routes/LoyaltyEvent/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { routeCreator as productGroupRouteCreator } from '@occc-app/routes/ProductGroup/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  SectionCard,
  renderNodeGroups,
  ItemsOrViewAll,
  arrangeNodeGroups,
} from '@occc-app/routes/CampaignComponents/component-details';

import { formatThreshold, mapAwardToText } from './configuration-details.utils';
import { countProductsByLabels } from './configuration-details.constants';
import {
  ConfigurationDetailsAward,
  ConfigurationDetailsMarketingContent,
  ConfigurationDetailsQualifyingEvent,
} from './configuration-details.types';

const BLOCKS_IN_ROW = 4;

export type ConfigurationDetailsProps = {
  senderAward: ConfigurationDetailsAward;
  receiverAward: ConfigurationDetailsAward;
  qualifyingEvent: ConfigurationDetailsQualifyingEvent;
  marketingContents: ConfigurationDetailsMarketingContent[];
};

export const ConfigurationDetails = (props: ConfigurationDetailsProps) => {
  const { senderAward, receiverAward, qualifyingEvent, marketingContents } = props;

  const blockGroups = useMemo(
    () =>
      arrangeNodeGroups(
        [
          <InfoField label='Give Sender' value={mapAwardToText(senderAward)} />,
          <InfoField label='Give Receiver' value={mapAwardToText(receiverAward)} />,
          qualifyingEvent.type === 'transaction' && [
            <InfoField label='When Receiver' value='Makes a Purchase' />,
            <InfoField
              label='Purchase Amount'
              value={formatThreshold({
                threshold: qualifyingEvent.threshold,
                countProductsBy: qualifyingEvent.countProductsBy,
              })}
            />,
            <InfoField
              label='Count Products By'
              value={countProductsByLabels[qualifyingEvent.countProductsBy]}
            />,
            <ItemsOrViewAll
              label='Product Purchased'
              itemName='Product Group'
              items={qualifyingEvent.productGroups.map((pg) => ({
                id: pg.id,
                label: pg.name,
                href: productGroupRouteCreator.details(pg.id),
              }))}
            />,
          ],
          qualifyingEvent.type === 'loyaltyEvent' && [
            <InfoField label='When Receiver' value='Completed a Loyalty Event' />,
            <InfoField
              label='Loyalty Event'
              value={
                <LinkChip
                  label={qualifyingEvent.loyaltyEventOffer.name}
                  href={loyaltyEventRouteCreator.details(qualifyingEvent.loyaltyEventOffer.id)}
                />
              }
            />,
          ],
        ],
        BLOCKS_IN_ROW,
      ),
    [qualifyingEvent, receiverAward, senderAward],
  );

  const marketingContentsBlockGroups = useMemo(
    () =>
      arrangeNodeGroups(
        marketingContents.map((content) => [
          content.subBrandName ? (
            <Typography variant='subtitle1' color='text.primary' gutterBottom>
              {content.subBrandName}
            </Typography>
          ) : null,
          <InfoField label='Instructions for Sender' value={content.senderInstructions} />,
          <InfoField label='Instructions for Receiver' value={content.receiverInstructions} />,
          <InfoField label='Text Message for Receiver' value={content.smsTemplate} />,
        ]),
        BLOCKS_IN_ROW,
      ),
    [marketingContents],
  );

  return (
    <SectionCard title='Configuration'>
      {renderNodeGroups(blockGroups, BLOCKS_IN_ROW)}
      <Divider />
      {renderNodeGroups(marketingContentsBlockGroups, BLOCKS_IN_ROW, true)}
    </SectionCard>
  );
};
